// @flow
import React, { useMemo } from 'react';
import classNames from 'classnames';
import { get, has, isEmpty, find } from 'lodash';
import moment from 'moment';
import { STATUS_ICONS } from '../Calendar/status';

type Props = {
  appointment: Object,
  clinicDoctors: Object,
  clinic: Object,
  filter: Object,
  loadingItems: Object,
  isFinatialEnabled: boolean,
  onClick: Function,
  _: Function,
};

const AppointmentCard = ({
  appointment, clinicDoctors, clinic, filter, loadingItems,
  isFinatialEnabled, onClick, _,
}: Props) => {
  const isLoading = useMemo(
    () => get(loadingItems, String(get(appointment, 'id', '')), false),
    [loadingItems, appointment],
  );

  const badge = useMemo(() => get(appointment, 'badge', false), [appointment]);

  const highPrivacyBlocked = useMemo(
    () => get(appointment, 'restricted', false),
    [appointment],
  );

  const restricted = useMemo(
    () => appointment.restricted || highPrivacyBlocked,
    [appointment, highPrivacyBlocked],
  );

  const eventColor = useMemo(
    () => get(clinicDoctors, [appointment.doctorId, 'color']),
    [clinicDoctors, appointment],
  );

  const indebt = useMemo(
    () => (isFinatialEnabled ? get(appointment, ['patient', 'indebt'], false) && !highPrivacyBlocked : false),
    [isFinatialEnabled, appointment, highPrivacyBlocked],
  );

  const withoutPatient = useMemo(
    () => appointment.withoutPatient || isEmpty(appointment.patient),
    [appointment],
  );

  const startTime = useMemo(
    () => moment.utc(appointment.start).format('HH:mm'),
    [appointment],
  );

  const endTime = useMemo(() => {
    const start = moment.utc(appointment.start);
    const duration = parseInt(appointment.duration, 10) || 30;
    return start.clone().add(duration, 'minutes').format('HH:mm');
  }, [appointment]);

  const selectedRoomName = useMemo(() => {
    if (restricted) return '';
    if (get(clinic, ['numberOfRooms']) === 1) return '';
    if (!get(filter, ['showAllRooms'], false)) return '';
    const clinicRooms = get(clinic, ['rooms']);
    const selectedRoom = find(clinicRooms, (room) => room.number === appointment.room);
    return get(selectedRoom, 'name', '');
  }, [restricted, clinic, filter, appointment]);

  const handleClick = () => {
    if (restricted) return;
    onClick(appointment);
  };

  return (
    <div
      className={classNames('android-appointment-card', {
        'card-restricted': restricted,
        'card-canceled': !restricted && badge === 'canceled',
        'card-without-patient': !restricted && withoutPatient,
        'card-loading': isLoading,
      }, eventColor ? `card-color-${eventColor}` : null)}
      onClick={handleClick}
    >
      <div
        className={classNames('card-color-bar', eventColor ? `bar-color-${eventColor}` : null)}
      />
      <div className="card-body">
        <div className="card-time">
          <span className="time-range">{startTime} - {endTime}</span>
          <span className="time-duration">{appointment.duration || 30} min</span>
        </div>
        <div className="card-info">
          <div className="card-title-row">
            {badge && has(STATUS_ICONS, badge) && (
              <svg className={classNames('svgicon card-status-icon', badge)}>
                <use xlinkHref={`#${get(STATUS_ICONS, badge)}`} />
              </svg>
            )}
            {get(appointment, 'online') && (
              <svg className="svgicon card-online-icon"><use xlinkHref="#videocam" /></svg>
            )}
            <span className={classNames('card-title', { 'title-restricted': restricted })}>
              {restricted ? _('label/blocked_time') : (appointment.title || _('label/blocked_time'))}
            </span>
            {!restricted && indebt && <span className="notifyDot" />}
          </div>
          {!restricted && get(appointment, 'notes') && (
            <div className="card-notes">{appointment.notes}</div>
          )}
          {!restricted && selectedRoomName && (
            <div className="card-room">{selectedRoomName}</div>
          )}
        </div>
        {isLoading && (
          <div className="card-spinner">
            <svg className="svgicon" fill="rgb(0, 0, 0)" style={{ width: 16, height: 16 }}>
              <use xlinkHref="#spinner" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

// $FlowFixMe
export default React.memo(AppointmentCard);
